import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function Contact() {
  const { t } = useTranslation();

  const infoAnim = useScrollAnimation();
  const servicesAnim = useScrollAnimation();
  const mapAnim = useScrollAnimation();

  const serviceInquiries = [
    { key: 'mechanical', icon: 'ri-settings-3-line' },
    { key: 'electricity', icon: 'ri-flashlight-line' },
    { key: 'hydraulic', icon: 'ri-water-flash-line' },
    { key: 'electromechanics', icon: 'ri-building-2-line' },
    { key: 'hvac', icon: 'ri-windy-line' },
    { key: 'electronics', icon: 'ri-cpu-line' },
    { key: 'tickets', icon: 'ri-plane-line' },
    { key: 'tourism', icon: 'ri-map-pin-2-line' },
    { key: 'office', icon: 'ri-stack-line' },
    { key: 'visas', icon: 'ri-passport-line' },
    { key: 'rentacar', icon: 'ri-car-line' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[360px] md:h-[480px] flex items-center justify-center bg-primary-950">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20cityscape%20of%20Luanda%20Angola%20with%20warm%20sunset%20lighting%2C%20steel%20blue%20sky%20and%20copper%20sunset%20tones%2C%20clean%20minimalist%20architectural%20composition%20with%20diverse%20urban%20landscape%2C%20abstract%20geometric%20design&width=1600&height=900&seq=contact-hero-deiaspart&orientation=landscape"
            alt="Contact DEIASPART"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading font-bold text-3xl md:text-5xl lg:text-6xl text-background-50 mb-3">
            {t('contact.hero.title')}
          </h1>
          <p className="text-background-300 text-base md:text-lg max-w-xl mx-auto">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={infoAnim.ref}
            className={`max-w-5xl mx-auto transition-all duration-700 ${
              infoAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10 text-center">
              {t('contact.info.title')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {/* Address */}
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <i className="ri-map-pin-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-2">
                  {t('contact.info.address')}
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {t('contact.info.addressValue')}
                </p>
              </div>

              {/* Phones */}
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <i className="ri-phone-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-2">
                  {t('contact.info.phones')}
                </h3>
                <div className="space-y-1">
                  <p className="text-foreground-600 text-sm">222 744 200</p>
                  <p className="text-foreground-600 text-sm">923 392 218</p>
                  <p className="text-foreground-600 text-sm">934 765 583</p>
                </div>
              </div>

              {/* Emails */}
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <i className="ri-mail-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-2">
                  {t('contact.info.emails')}
                </h3>
                <div className="space-y-1">
                  <a href="mailto:geral@deiaspart.co.ao" className="text-primary-500 hover:text-primary-600 text-sm block transition-colors">
                    geral@deiaspart.co.ao
                  </a>
                  <a href="mailto:rodrigo.deia@deiaspart.co.ao" className="text-primary-500 hover:text-primary-600 text-sm block transition-colors">
                    rodrigo.deia@deiaspart.co.ao
                  </a>
                </div>
              </div>

              {/* NIF */}
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <i className="ri-file-text-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-2">
                  {t('contact.info.nif')}
                </h3>
                <p className="text-foreground-600 text-sm">5417635634</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Inquiries */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={servicesAnim.ref}
            className={`max-w-5xl mx-auto transition-all duration-700 ${
              servicesAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                {t('contact.services.title')}
              </h2>
              <p className="text-foreground-600 text-base">
                {t('contact.services.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {serviceInquiries.map((service, i) => (
                <a
                  key={service.key}
                  href={`mailto:geral@deiaspart.co.ao?subject=${encodeURIComponent(t('contact.services.emailSubject') + t(`service.${service.key}.name`))}`}
                  className={`flex items-center gap-4 bg-background-50 rounded-xl p-4 md:p-5 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50 transition-all duration-300 group ${
                    servicesAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-50 group-hover:bg-primary-100 flex items-center justify-center shrink-0 transition-colors">
                    <i className={`${service.icon} text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-medium text-foreground-950 text-sm whitespace-nowrap">
                      {t(`service.${service.key}.name`)}
                    </h4>
                    <p className="text-foreground-500 text-xs mt-0.5">
                      geral@deiaspart.co.ao
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-300 group-hover:text-primary-500 ml-auto w-4 h-4 flex items-center justify-center shrink-0 transition-colors"></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div
            ref={mapAnim.ref}
            className={`max-w-5xl mx-auto transition-all duration-700 ${
              mapAnim.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6 text-center">
              {t('contact.cta.title')}
            </h2>
            <p className="text-foreground-600 text-center mb-8 max-w-2xl mx-auto">
              {t('contact.cta.text')}
            </p>
            <div className="rounded-2xl overflow-hidden border border-background-200/70 aspect-[16/9] md:aspect-[21/9]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3941.5!2d13.2894!3d-8.8390!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zOMKwNTAnMTAuNCJTIDEzwrAxNyczNi4yIkE!5e0!3m2!1spt!2sao!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="DEIASPART Location"
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}